// Copy of PremiumDueReport.js modified for Lapsed Policies Report
import React, { useState, useEffect, useRef } from "react";
import { FiChevronDown } from "react-icons/fi";
import { Mail } from "lucide-react";
import { FaFilePdf, FaRegFilePdf } from "react-icons/fa";
import { RiFileExcel2Line } from "react-icons/ri";
import { Formik, Form, Field } from "formik";
import { useNavigate } from "react-router-dom";
import { useGetGroupsQuery, useLazyPreviewPolicyReportQuery, useCreateExportJob, useExportJobStatus, useDownloadExport } from "../../../../../services/redux/api/components";
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { API_BASE_URL } from '../../../../../services/config';
import ReportLoader from '../LifePolicyRegister/ReportLoader';


/* Tailwind tokens (ensure you have brandRed in tailwind.config.js) */
const fieldBase =
    "rounded-md border border-gray-200 px-3 py-2 shadow-sm focus:outline-none focus:ring-1 focus:ring-sky-200 ";
const labelBase = " text-gray-700";
const sectionHeader = "bg-gray-100 p-3 rounded-md  font-semibold text-gray-600";
const btnPrimary =
    "bg-brandRed hover:bg-red-600 text-white  px-6 py-2 rounded-md shadow-md transition";

function useOutsideClick(ref, handler) {
    useEffect(() => {
        const onClick = (e) => {
            if (!ref.current) return;
            if (!ref.current.contains(e.target)) handler();
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [ref, handler]);
}

/**
 * Normalize mutation/query hook shapes:
 * - RTK-toolkit codegen often returns: [trigger, result]
 * - Some setups return an object: { trigger, data, isLoading, error, ... }
 * This helper returns { trigger, result } consistently.
 */
function normalizeHook(hook) {
  let trigger;
  let result = {};

  if (Array.isArray(hook)) {
    trigger = hook[0];
    result = hook[1] ?? {};
  } else if (hook && typeof hook === 'object') {
    trigger = hook.trigger ?? hook.mutate ?? hook;
    result = {
      data: hook.data,
      isLoading: hook.isLoading ?? hook.isFetching ?? hook.status === 'pending',
      error: hook.error,
      status: hook.status,
    };
  } else {
    trigger = undefined;
    result = {};
  }

  return { trigger, result };
}

function MultiSelectInline({ options = [], value = [], onChange, placeholder = "Select groups..." }) {
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const rootRef = useRef(null);
    useOutsideClick(rootRef, () => setOpen(false));

    const toggle = (id) => {
        if (value.includes(id)) onChange(value.filter((v) => v !== id));
        else onChange([...value, id]);
    };

    const filteredOptions = options.filter((o) =>
        o.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div ref={rootRef} className="relative w-full">
            <button
                type="button"
                onClick={() => setOpen((s) => !s)}
                className={`${fieldBase} min-h-[40px] py-2 w-full flex items-center gap-2 justify-between bg-white`}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <div className="flex items-center gap-2 flex-1 flex-wrap max-h-24 overflow-y-auto">
                    {value.length === 0 ? (
                        <span className="text-gray-400  truncate">{placeholder}</span>
                    ) : (
                        <div className="flex items-center gap-1 flex-wrap">
                            {value.map((id) => {
                                const o = options.find((x) => x.id === id);
                                return (
                                    <span
                                        key={id}
                                        className=" bg-gray-100 px-2 py-0.5 rounded-md text-gray-700 whitespace-nowrap text-sm"
                                    >
                                        {o?.label ?? id}
                                    </span>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                    <span className=" text-gray-500 text-sm">{value.length > 0 ? `${value.length} selected` : ""}</span>
                    <FiChevronDown className="text-gray-500" />
                </div>
            </button>

            {open && (
                <div className="absolute z-30 mt-2 w-full bg-white border border-gray-200 shadow-lg rounded-md overflow-hidden">
                    <div className="p-2 border-b border-gray-200">
                        <input
                            type="text"
                            placeholder="Search groups..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                    <div className="max-h-52 overflow-auto p-2">
                        {filteredOptions.length === 0 ? (
                            <div className="text-gray-400 text-center py-2">No groups found</div>
                        ) : (
                            filteredOptions.map((o) => (
                                <label key={o.id} className="flex items-center gap-3 px-2 py-2 hover:bg-gray-50 rounded-md cursor-pointer">
                                    <input type="checkbox" checked={value.includes(o.id)} onChange={() => toggle(o.id)} className="w-4 h-4" />
                                    <span className="">{o.label}</span>
                                </label>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default function LapsedPoliciesReport() {
    const [showPdfModal, setShowPdfModal] = React.useState(false);
    const [pdfObjectUrl, setPdfObjectUrl] = React.useState(null);
    const [isGenerating, setIsGenerating] = React.useState(false);
    const [triggerPreview, previewResult] = useLazyPreviewPolicyReportQuery();
    const [jobId, setJobId] = React.useState(null);
    const [jobStatusText, setJobStatusText] = React.useState('');
    const [jobCreating, setJobCreating] = React.useState(false);

    // Export job hooks using normalizeHook pattern
    const createJobHook = useCreateExportJob();
    const { trigger: triggerCreateJob } = normalizeHook(createJobHook);
    
    const jobStatusQuery = useExportJobStatus(jobId, { pollingInterval: 2000, skip: !jobId });
    
    const downloadHook = useDownloadExport();
    const { trigger: triggerDownload } = normalizeHook(downloadHook);

    // Formik will manage form state via initialValues
    const initialValues = {
        fromLapsedDate: "",
        toLapsedDate: "",
        paymentMode: "",
        policyStatus: "",
        groupsSelected: [],
        sortBy: "groupwise",
        includeAddress: false,
        includeMobile: false,
        includeEmail: false,
        // reportFields: { address: false, mobile: false, email: false },
    };

    const navigate = useNavigate();

    const { data: groupsResult, isFetching: groupsLoading } = useGetGroupsQuery({ pageNumber: 1, pageSize: 1000 });

    const groupsOptions = (
        groupsResult?.data?.map((g) => ({ id: String(g.GroupId ?? g.id ?? ''), label: g.GroupName ?? g.GroupCode ?? String(g.GroupID ?? g.id ?? '') })) || []
    );

    const buildPayload = (values) => {
        const payload = {
            policyStatus: 'lapsed',
            sortBy: values.sortBy || 'groupwise',
            includeAddress: values.includeAddress ? '1' : '0',
            includeMobile: values.includeMobile ? '1' : '0',
            includeEmail: values.includeEmail ? '1' : '0',
            filterByDate: 'fup',
            includeLapsed: '1',
            reportTitle: 'Lapsed Policies Report',
            sortingOrder: values.sortBy || 'groupwise'
        };
        
        // Only add optional parameters if they have values
        if (values.fromLapsedDate) payload.fromDate = values.fromLapsedDate;
        if (values.toLapsedDate) payload.toDate = values.toLapsedDate;
        if (values.paymentMode) payload.paymentMode = values.paymentMode;
        if (values.groupsSelected && values.groupsSelected.length) {
            payload.groupIds = values.groupsSelected.join(',');
        }
        
        return payload;
    };

    // Monitor job status
    useEffect(() => {
        if (jobStatusQuery?.data?.job) {
            const j = jobStatusQuery.data.job;
            setJobStatusText(j.status + (j.error ? ` — ${j.error}` : ''));
            if (j.status === 'done' && j.filePath) {
                downloadGeneratedFile(jobId).catch((e) => console.error('download error', e));
            } else if (j.status === 'failed') {
                setJobCreating(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobStatusQuery?.data]);

    // Download generated file
    const downloadGeneratedFile = async (id) => {
        try {
            if (triggerDownload) {
                const dlRes = await (triggerDownload(id).unwrap?.() ?? triggerDownload(id));
                if (dlRes instanceof Blob) {
                    const url = URL.createObjectURL(dlRes);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `lapsed_policies_${id}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                } else if (dlRes?.url) {
                    window.location.href = dlRes.url;
                } else {
                    window.location.href = `${API_BASE_URL}/policy-management/export/download/${id}`;
                }
            } else {
                window.location.href = `${API_BASE_URL}/policy-management/export/download/${id}`;
            }
        } catch (err) {
            console.error('Error downloading file', err);
            window.location.href = `${API_BASE_URL}/policy-management/export/download/${id}`;
        }
    };

    // Create export job and download
    const handleCreateExportJobAndDownload = async (values) => {
        setJobCreating(true);
        setJobStatusText('Creating export job...');
        try {
            if (pdfObjectUrl) {
                const filename = `lapsed_policies_preview_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.pdf`;
                const a = document.createElement('a');
                a.href = pdfObjectUrl;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setJobCreating(false);
                setJobStatusText('Downloaded preview PDF');
                return;
            }
            if (typeof triggerCreateJob !== 'function') throw new Error('Create job hook not available');
            const exportParams = buildPayload(values);
            exportParams.reportTitle = 'Lapsed Policies Report';
            exportParams.reportType = 'lapsed-policies';
            exportParams.format = 'pdf';
            const res = await (triggerCreateJob(exportParams).unwrap?.() ?? triggerCreateJob(exportParams));
            const payload = res?.data ?? res;
            const newJobId = payload?.jobId ?? payload?.job?.id ?? payload?.id;
            if (!newJobId) throw new Error('No jobId returned from create export job');
            setJobId(newJobId);
            setJobStatusText(`Job created: ${newJobId}. Waiting for file...`);
        } catch (err) {
            console.error('Export job error', err);
            alert('Export job failed: ' + (err?.message ?? err));
            setJobCreating(false);
        }
    };

    // Cleanup effect for PDF object URL
    React.useEffect(() => () => { if (pdfObjectUrl) URL.revokeObjectURL(pdfObjectUrl); }, [pdfObjectUrl]);

    const handlePdfPreview = async (values) => {
        try {
            setIsGenerating(true);
            const payload = buildPayload(values);
            const result = await triggerPreview(payload).unwrap();
            
            if (result && result.pdf) {
                const base64 = result.pdf;
                const byteCharacters = atob(base64);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const blob = new Blob([new Uint8Array(byteNumbers)], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                setPdfObjectUrl(url);
                setShowPdfModal(true);
            }
        } catch (error) {
            console.error('PDF preview error:', error);
            alert('Failed to generate PDF preview. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleExcelExport = async (values) => {
        try {
            setIsGenerating(true);
            const payload = buildPayload(values);
            
            // Fetch data using the policy report list endpoint
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}/api/policy-management/policy-report/list?` + new URLSearchParams({
                pageNumber: 1,
                pageSize: 10000,
                ...payload
            }), {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                }
            });
            
            const data = await response.json();
            const rows = Array.isArray(data) ? data : (data?.data ?? []);
            
            if (rows.length === 0) {
                alert('No data available for export');
                return;
            }

            // Create Excel workbook
            const wb = new ExcelJS.Workbook();
            const ws = wb.addWorksheet('Lapsed Policies Report');

            // Define columns
            const columns = [
                { header: 'Group Code', key: 'GroupCode', width: 20 },
                { header: 'Area', key: 'Area', width: 15 },
                { header: 'Life Assured', key: 'LifeAssured', width: 25 },
                { header: 'Policy No', key: 'PolicyNo', width: 20 },
                { header: 'Policy Status', key: 'PolicyStatus', width: 15 },
                { header: 'Product', key: 'Product', width: 25 },
                { header: 'Term/PPT', key: 'TermPPT', width: 12 },
                { header: 'Commencement Date', key: 'CommencementDate', width: 18 },
                { header: 'FUP Date', key: 'FUPDate', width: 15 },
                { header: 'Sum Assured', key: 'SumAssured', width: 15 },
                { header: 'Payment Mode', key: 'PaymentMode', width: 15 },
                { header: 'Net Premium', key: 'NetPremium', width: 15 },
                { header: 'Agency', key: 'Agency', width: 20 },
            ];

            if (values.includeAddress) columns.push({ header: 'Address', key: 'Address', width: 30 });
            if (values.includeMobile) columns.push({ header: 'Mobile', key: 'Mobile', width: 15 });
            if (values.includeEmail) columns.push({ header: 'Email', key: 'Email', width: 25 });

            ws.columns = columns;

            // Style header
            const headerRow = ws.getRow(1);
            headerRow.font = { bold: true, size: 12 };
            headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2CC' } };
            headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

            // Add data rows
            rows.forEach(row => {
                ws.addRow({
                    GroupCode: row.GroupCode || row.Group || '',
                    Area: row.Area || '',
                    LifeAssured: row.LifeAssured || row.LifeAssuredName || '',
                    PolicyNo: row.PolicyNo || row.PolicyNumber || '',
                    PolicyStatus: row.PolicyStatus || row.Status || '',
                    Product: row.Product || row.ProductName || '',
                    TermPPT: row.TermPPT || row.Term || '',
                    CommencementDate: row.CommencementDate || row.CommDate || '',
                    FUPDate: row.FUPDate || '',
                    SumAssured: row.SumAssured || '',
                    PaymentMode: row.PaymentMode || '',
                    NetPremium: row.NetPremium || '',
                    Agency: row.Agency || '',
                    Address: row.Address || '',
                    Mobile: row.Mobile || '',
                    Email: row.Email || ''
                });
            });

            // Generate and download
            const buffer = await wb.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, `Lapsed_Policies_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch (error) {
            console.error('Excel export error:', error);
            alert('Failed to generate Excel report. Please try again.');
        } finally {
            setIsGenerating(false);
        }
    };

    React.useEffect(() => {
        return () => {
            if (pdfObjectUrl) {
                URL.revokeObjectURL(pdfObjectUrl);
            }
        };
    }, [pdfObjectUrl]);

    const handleGenerate = (values) => {
        const payload = {
            // map local form fields to API expected names
            fromDate: values.fromLapsedDate || null,
            toDate: values.toLapsedDate || null,
            paymentMode: values.paymentMode || null,
            // force server-side filtering to only 'lapsed' policies
            policyStatus: 'lapsed',
            groupIds: values.groupsSelected && values.groupsSelected.length ? values.groupsSelected.join(',') : null,
            sortBy: values.sortBy || null,
            includeAddress: values.includeAddress || false,
            includeMobile: values.includeMobile || false,
            includeEmail: values.includeEmail || false,
            includeLapsed: 1, // must be truthy so server does not exclude 'lapsed' rows during post-filter
            filterByDate: "FUPDate", // always filter by FUP date for Lapsed Policies Report
        };
        navigate('/lapsed-policies-report-preview', { state: { payload } });
    };

    return (
        <div className="w-full p-4">
            <div style={{ boxShadow: "0 0 8px #ccc" }} className="w-full rounded bg-white">
              <div className="p-2 border-b border-brandRed">
                      <div className="flex justify-between items-center">
                        <h5 className="text-brandRed font-bold m-3">Lapsed Report</h5>
                        <Formik initialValues={initialValues} onSubmit={handleGenerate}>
                          {({ values }) => (
                            <div className="flex gap-3 items-center">
                              <Mail size={38} className="text-brandRed cursor-not-allowed opacity-50" />
                              <RiFileExcel2Line 
                                size={38} 
                                className="text-green-700 cursor-pointer hover:scale-110 transition" 
                                onClick={() => handleExcelExport(values)}
                                title="Download Excel Report"
                              />
                              <FaFilePdf 
                                size={37} 
                                className="text-red-600 cursor-pointer hover:scale-110 transition" 
                                onClick={() => handlePdfPreview(values)}
                                title="Preview PDF Report"
                              />
                            </div>
                          )}
                        </Formik>
                      </div>
                    </div>

                <Formik initialValues={initialValues} onSubmit={handleGenerate}>
                    {({ values, setFieldValue }) => (
                        <Form className="w-full">
                            <div className="p-6">
                                <h6 className="text-gray-700 font-bold my-3 bg-gray-100 rounded p-2">Filter Selection</h6>

                                {/* ===== INPUT ROW - From / To date fields (single clean row) ===== */}
                                <div className="grid grid-cols-12 gap-3 items-center p-2">
                                    <div className="col-span-12 lg:col-span-2">
                                        <div className={`${labelBase} mb-0.5`}>Lapsed Period</div>
                                    </div>

                                    <div className="col-span-12 lg:col-span-8 flex flex-wrap gap-3 items-center">
                                        <div className="flex items-center gap-2">
                                            <span className=" text-gray-700">From</span>
                                            <Field type="date" name="fromLapsedDate" className={`${fieldBase} h-10 w-52`} aria-label="From date" />
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <span className=" text-gray-700">To</span>
                                            <Field type="date" name="toLapsedDate" className={`${fieldBase} h-10 w-52`} aria-label="To date" />
                                        </div>
                                    </div>
                                </div>

                                {/* Sorting order */}
                                <h6 className="text-gray-700 font-bold mb-3 my-3 bg-gray-100 rounded p-2 ">Sorting Order</h6>
                                <div className="flex flex-wrap items-center gap-6  p-3">
                                    <label className="flex items-center gap-2">
                                        <Field type="radio" name="sortBy" value="groupwise" className="w-4 h-4" />
                                        <span className="ml-1  text-gray-700">Group wise</span>
                                    </label>

                                    <label className="flex items-center gap-2">
                                        <Field type="radio" name="sortBy" value="memberwise" className="w-4 h-4" />
                                        <span className="ml-1  text-gray-700">Member wise</span>
                                    </label>

                                    <label className="flex items-center gap-2">
                                        <Field type="radio" name="sortBy" value="areawise" className="w-4 h-4" />
                                        <span className="ml-1  text-gray-700">Area wise</span>
                                    </label>

                                    <label className="flex items-center gap-2">
                                        <Field type="radio" name="sortBy" value="policynowise" className="w-4 h-4" />
                                        <span className="ml-1  text-gray-700">Policy No wise</span>
                                    </label>


                                </div>

                                <div className="grid grid-cols-12 gap-3 items-center p-2">
                                    <div className="col-span-12 lg:col-span-2">
                                        <div className={`${labelBase} mb-0.5`}>Select Groups</div>
                                    </div>

                                    <div className="col-span-12 lg:col-span-6 ">
                                        <MultiSelectInline
                                            options={groupsOptions}
                                            value={values.groupsSelected}
                                            onChange={(v) => setFieldValue("groupsSelected", v)}
                                            placeholder="Select groups..."
                                        />
                                    </div>

                                    <div className="col-span-12 lg:col-span-4 flex items-center lg:justify-start justify-end">
                                        {values.groupsSelected.length > 0 && (
                                            <div className="hidden lg:block  text-blue-600 hover:underline ml-3">
                                                {values.groupsSelected.length} Groups Selected
                                            </div>
                                        )}
                                    </div>

                                    {values.groupsSelected.length > 0 && (
                                        <div className="col-span-12 lg:hidden  text-center mt-2 text-blue-600 hover:underline">
                                            Groups Selected: {values.groupsSelected.length}
                                        </div>
                                    )}
                                </div>

                                {/* Add in Report */}
                                <h6 className="text-gray-700 font-bold mb-3 my-3 bg-gray-100 rounded p-2">Add in Report</h6>
                                <div className="flex gap-6 items-center py-4 flex-wrap p-3">
                                    <label className="flex items-center gap-2">
                                        <Field type="checkbox" name="includeAddress" className="w-4 h-4" />
                                        <span className="ml-1  text-gray-700">Address</span>
                                    </label>

                                    <label className="flex items-center gap-2">
                                        <Field type="checkbox" name="includeMobile" className="w-4 h-4" />
                                        <span className="ml-1  text-gray-700">Mobile</span>
                                    </label>

                                    <label className="flex items-center gap-2">
                                        <Field type="checkbox" name="includeEmail" className="w-4 h-4" />
                                        <span className="ml-1  text-gray-700">Email</span>
                                    </label>
                                </div>

                                <div className="flex justify-center mt-8">
                                    <button type="submit" className={btnPrimary}>
                                        Generate Report
                                    </button>
                                </div>

                                <div className="mt-6 text-center  text-gray-500">Lapsed Policies Report..</div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </div>

            {/* PDF Preview Modal */}
            {showPdfModal && (
                <div className="fixed inset-0 z-50 flex items-start justify-center p-6 bg-black bg-opacity-40">
                    <div className="bg-white rounded-md w-full max-w-6xl h-[80vh] overflow-hidden shadow-lg">
                        <div className="flex items-center justify-between p-3 border-b">
                            <div className="flex items-center gap-3">
                                <FaRegFilePdf className="text-red-600" />
                                <div>
                                    <div className="font-semibold">PDF Preview</div>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <div className="text-sm text-gray-600 mr-4">{jobStatusText}</div>
                                <button className="px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700" onClick={() => handleCreateExportJobAndDownload(initialValues)} disabled={jobCreating}>
                                    {jobCreating ? 'Preparing...' : 'Download'}
                                </button>
                                <button
                                    className="px-3 py-1 bg-red-600 text-white rounded text-sm"
                                    onClick={() => {
                                        setShowPdfModal(false);
                                        if (pdfObjectUrl) {
                                            URL.revokeObjectURL(pdfObjectUrl);
                                            setPdfObjectUrl(null);
                                        }
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>

                        <div className="h-full">
                            {previewResult?.isLoading || previewResult?.isFetching ? (
                                <div className="flex items-center justify-center h-full">Generating Preview...</div>
                            ) : (
                                <iframe title="PDF Preview" src={pdfObjectUrl} width="100%" height="100%" style={{ border: 'none' }} />
                            )}
                        </div>
                    </div>
                </div>
            )}

            <ReportLoader visible={isGenerating} message="PDF report is generating..." />
        </div>
    );
}
